/**
 * FsmSelect — the object-type + state-machine chooser above the FSM canvas
 * (phase 6d, step 2).
 *
 * Direct TS port of `components/FSM/select.js`. Two `MerlinSelect` dropdowns:
 *   - Object: pick an object type; selecting one reveals that object type's
 *     code-generation FSM and selects the class so the inspector follows
 *     (EDG/explorer parity).
 *   - State machine: pick which FSM of that object type to show, and select
 *     that FSM so the inspector follows (explorer parity).
 *
 * The visible FSM is driven by the SAME `ui.visible.showFSM` flow the old select
 * bar used (via `useUpdateVisibleFSM`), so the canvas re-derives from the chosen
 * FSM's view. Selection goes through `onSelect` (confirmable) so a dirty
 * inspector is guarded the same way as an EDG click. The markup + `fsm-select`
 * classes are unchanged so the existing styling (the kept-as-CSS parts in
 * fsmFlow.css plus the converted utilities here) applies verbatim.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { observer } from 'mobx-react';
import MerlinSelect from '../components/MerlinSelect';
// useUpdateVisibleFSM is an untyped JS hook; the model boundary is `any`.
import { useUpdateVisibleFSM } from '../hooks';
import { applyFsmChooserChange } from './fsmChooser';

export type FsmSelectProps = {
  /** The MERODE model (`version.content`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any;
  /**
   * Select the chosen object type / FSM so the inspector follows (toggle=false,
   * openInspector=true). Routed through ProjectVersion's confirmable Select.
   */
  onSelect: (id: string, toggle?: boolean, openInspector?: boolean) => void;
};

const FsmSelect = observer(function FsmSelect({ model, onSelect }: FsmSelectProps) {
  const { fsm, showFSM } = useUpdateVisibleFSM(model);
  const objectType = fsm != null ? fsm.objectType : undefined;

  const objectTypeOptions = model.metamodel.objectTypes.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ot: any) => ({ label: ot.name, value: ot.id })
  );

  const fsmOptions =
    objectType != null
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        objectType.fsms.map((f: any) => ({ label: f.name, value: f.id }))
      : [];

  const flow = {
    getObjectTypeById: (id: string) => model.metamodel.getObjectTypeById(id),
    showFSM,
    select: (id: string) => onSelect(id, false, true),
  };

  const onChangeObjectType = (option: { value?: unknown } | null) => {
    if (option == null || option.value == null) {
      return;
    }
    applyFsmChooserChange(flow, { objectTypeId: option.value as string });
  };

  const onChangeFSM = (option: { value?: unknown } | null) => {
    if (option == null || option.value == null) {
      return;
    }
    applyFsmChooserChange(flow, { fsmId: option.value as string });
  };

  return (
    <div className="fsm-select flex h-[45px] w-full items-center justify-start rounded-t-[4px] bg-white px-[15px] align-middle shadow-[inset_0_-1px_0_0_var(--color-grey)]">
      <div className="select-object-type flex items-center">
        <label className="rs-element rs-form-label whitespace-nowrap">Object: </label>
        <div className="select-wrapper ml-[5px]">
          <MerlinSelect
            className="rs-input-wrap"
            options={objectTypeOptions}
            value={objectType != null ? objectType.id : ''}
            onChange={onChangeObjectType}
            searchable={false}
            clearable={false}
          />
        </div>
      </div>
      <div className="separator mr-[15px] ml-[5px] h-[45px] overflow-hidden border-r border-grey indent-[-9999px]">
        |
      </div>
      <div className="select-fsm flex items-center">
        <label className="rs-element rs-form-label whitespace-nowrap">State machine: </label>
        <div className="select-wrapper ml-[5px]">
          <MerlinSelect
            className="rs-input-wrap"
            options={fsmOptions}
            value={fsm != null ? fsm.id : ''}
            onChange={onChangeFSM}
            searchable={false}
            clearable={false}
          />
        </div>
      </div>
    </div>
  );
});

export default FsmSelect;
